import { api, LightningElement, wire } from "lwc";
import classNames from "classnames";

import {
  AnimationAdapter,
  AnimationAdapterValue,
} from "common/animationAdapter";
import { DEFAULT_SPRITE, Sprite } from "tds/icon";
import { attrToBool } from "common/helpers";

export type Size = "small" | "medium" | "large";
export type Variant =
  | "base"
  | "neutral"
  | "brand"
  | "tertiary"
  | "success"
  | "destructive"
  | "destructive-inverse"
  | "bare"
  | "bare-inverse"
  | "inverse";

export default class extends LightningElement {
  @api ariaLabel: string | null = null;
  @api ariaLive: string | null = null;
  @api ariaPressed: string | null = null;
  @api disabled: boolean | null = null;
  @api type: "submit" | null = null;
  @api variant: Variant = "neutral";
  @api size: Size = "medium";
  @api spinner: boolean = false;
  @api form: string | null = null;
  @api iconSprite: Sprite = DEFAULT_SPRITE;
  @api iconSymbol: string | null = null;

  @api
  focus() {
    this.template.querySelector("button,a")?.focus();
  }

  @wire(AnimationAdapter)
  private animation: AnimationAdapterValue = null!;

  private get hasSpinner() {
    return attrToBool(this.spinner);
  }

  get hasIcon() {
    return !!this._iconSymbol;
  }

  get _iconSprite() {
    return this.hasSpinner ? DEFAULT_SPRITE : this.iconSprite;
  }

  get _iconSymbol() {
    return this.hasSpinner ? "spinner" : this.iconSymbol;
  }

  get iconClassName() {
    return classNames("icon", { spinner: this.hasSpinner });
  }

  get sizeClassName() {
    return (["small", "large"] as Size[]).includes(this.size)
      ? `button--${this.size}`
      : null;
  }

  get className() {
    return classNames(
      "button",
      createVariantClassName(this.variant),
      this.sizeClassName,
      {
        "button--disabled": attrToBool(this.disabled),
        "button--animated": this.animation.enabled,
      }
    );
  }
}

export function createVariantClassName(variant: Variant) {
  const DENY_LIST = [
    "neutral",
    "tertiary",
    "destructive-inverse",
    "bare",
    "bare-inverse",
    "inverse",
  ];
  return `button--${variant} ${
    !DENY_LIST.includes(variant) ? "button--variant" : ""
  }`;
}
